"use client"

import type { SearchClient } from "instantsearch.js"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { Configure, InstantSearch, useHits, useSearchBox } from "react-instantsearch"

import { PRODUCT_INDEX_NAME, searchClient } from "@lib/search-client"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

type Hit = { objectID: string; title: string | null; handle: string | null; thumbnail: string | null }

const Suggestions = ({
  value,
  open,
  onNavigate,
}: {
  value: string
  open: boolean
  onNavigate: () => void
}) => {
  const timer = useRef<number | undefined>(undefined)
  const queryHook = useCallback((q: string, search: (v: string) => void) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => search(q), 200)
  }, [])
  const { refine } = useSearchBox({ queryHook })
  const { items } = useHits<Hit>()

  useEffect(() => {
    refine(value)
  }, [value, refine])

  if (!open || !value.trim() || !items.length) {
    return null
  }

  return (
    <ul
      className="absolute left-0 right-0 top-full mt-px bg-white text-[#0f1111] shadow-lg rounded-b-md z-50 py-1 max-h-[70vh] overflow-y-auto"
      data-testid="search-results"
    >
      {items.slice(0, 8).map((hit) =>
        hit.handle ? (
          <li key={hit.objectID}>
            <LocalizedClientLink
              href={`/products/${hit.handle}`}
              onClick={onNavigate}
              className="flex items-center gap-x-3 px-3 py-2 hover:bg-[#f3f3f3] text-sm"
            >
              <span className="relative w-9 h-9 shrink-0 bg-[#f7f7f7]">
                {hit.thumbnail && (
                  <Image src={hit.thumbnail} alt="" fill sizes="36px" className="object-cover" />
                )}
              </span>
              <span className="line-clamp-1 font-semibold">{hit.title}</span>
            </LocalizedClientLink>
          </li>
        ) : null
      )}
    </ul>
  )
}

const HeaderSearch = ({ categories }: { categories: { name: string; handle: string }[] }) => {
  const router = useRouter()
  const { countryCode } = useParams() as { countryCode: string }
  const [value, setValue] = useState("")
  const [category, setCategory] = useState("")
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLFormElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setOpen(false)
    const params = new URLSearchParams()
    if (value.trim()) {
      params.set(`${PRODUCT_INDEX_NAME}[query]`, value.trim())
    }
    if (category) {
      params.set(`${PRODUCT_INDEX_NAME}[refinementList][category][0]`, category)
    }
    router.push(`/${countryCode}/store${params.size ? `?${params.toString()}` : ""}`)
  }

  return (
    <form
      ref={wrapper}
      onSubmit={submit}
      role="search"
      className="relative flex flex-1 h-10 rounded-md focus-within:ring-[3px] focus-within:ring-amz-orange"
    >
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        aria-label="Categoria"
        className="hidden small:block h-full bg-[#e6e6e6] hover:bg-[#d4d4d4] text-[#555] text-xs pl-2 pr-1 rounded-l-md border-r border-[#cdcdcd] outline-none cursor-pointer max-w-[160px]"
      >
        <option value="">Todos los departamentos</option>
        {categories.map((c) => (
          <option key={c.handle} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>
      <input
        type="search"
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar en Amazona.es"
        aria-label="Buscar productos"
        className="flex-1 min-w-0 h-full px-3 text-[15px] text-[#0f1111] outline-none rounded-l-md small:rounded-none"
        data-testid="search-input"
      />
      <button
        type="submit"
        aria-label="Buscar"
        className="h-full w-[45px] bg-amz-orange hover:bg-amz-orange-dark rounded-r-md flex items-center justify-center"
        data-testid="nav-search-button"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2.5">
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M15.5 15.5 L21 21" strokeLinecap="round" />
        </svg>
      </button>
      <InstantSearch
        indexName={PRODUCT_INDEX_NAME}
        searchClient={searchClient as unknown as SearchClient}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Configure hitsPerPage={8} />
        <Suggestions value={value} open={open} onNavigate={() => setOpen(false)} />
      </InstantSearch>
    </form>
  )
}

export default HeaderSearch
